import { BrowserRouter, Routes, Route, Navigate, useLocation, Link } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import "@/App.css";
import Login from "@/pages/Login";
import OwnerDashboard from "@/pages/OwnerDashboard";
import Campaigns from "@/pages/Campaigns";
import Analytics from "@/pages/Analytics";
import Collections from "@/pages/Collections";
import OrgsUsers from "@/pages/OrgsUsers";
import Widget from "@/pages/Widget";
import CustomWidgets from "@/pages/CustomWidgets";
import FunctionalDemo from "@/pages/FunctionalDemo";
import VotingGuide from "@/pages/VotingGuide";
import VotingGuideManage from "@/pages/VotingGuideManage";
import ElectionSimulator from "@/pages/ElectionSimulator";
import GISTest from "@/pages/GISTest";
import MembershipWidget from "@/pages/MembershipWidget";
import PartyMembership from "@/pages/PartyMembership";
import Legal from "@/pages/Legal";
import { Toaster } from "@/components/ui/sonner";
import { loadOrgTheme } from "@/theme";
import Profile from "@/pages/Profile";
import { ChevronDown, Menu, LayoutDashboard, Users, FileText, User, LogOut } from "lucide-react";


const isOwner = () => {
  try { const t = localStorage.getItem('token'); if(!t) return false; const p = JSON.parse(atob(t.split('.')[1]||'')); return !!p.is_owner; } catch { return false; }
};

const RequireOwner = ({ children }) => {
  if (!localStorage.getItem('token')) return <Navigate to="/login" replace />;
  if (!isOwner()) return <Navigate to="/campaigns" replace />;
  return children;
};

const RequireAuth = ({ children }) => {
  const token = localStorage.getItem("token");
  return token ? children : <Navigate to="/login" replace />;
};

const tokenClaims = ()=>{ try{ const t=localStorage.getItem('token')||''; const p=JSON.parse(atob((t.split('.')[1]||''))); return p||{}; }catch{ return {}; } };
const tokenHasImp = ()=>{ const p=tokenClaims(); return !!p.imp; };
const isTPAUS = () => { const p = tokenClaims(); return p.org === '1897f5fb-38fe-4b4a-9a5f-b5acbc5767bd'; };
const ONP_WA_ORG_ID = 'f681cdf2-6f57-4b9b-af19-bd5297cdf348';
const isOnpWa = () => { const p = tokenClaims(); return p.org === ONP_WA_ORG_ID; };

const RequireOnpWa = ({ children }) => {
  if (!localStorage.getItem('token')) return <Navigate to="/login" replace />;
  if (!(isOwner() || isOnpWa())) return <Navigate to="/campaigns" replace />;
  return children;
};

// Navigation Dropdown Component with Art Deco styling
const NavDropdown = ({ hideHeaderButtons = false }) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const ownerMode = isOwner();
  const tpausMode = isTPAUS();
  const onpWaMode = isOnpWa();
  
  const menuItems = [
    // Owner-only: Custom Widgets (first item for owner)
    ...(ownerMode ? [{ href: '/custom-widgets', label: 'Custom Widgets', icon: LayoutDashboard, ownerOnly: true }] : []),
    // Owner Console (Orgs & Users management) - owner only
    ...(ownerMode ? [{ href: '/orgs-users', label: 'Owner Console', icon: Users, ownerOnly: true }] : []),
    // MPs & Electorates - owner only (goes to /owner dashboard)
    ...(ownerMode ? [{ href: '/owner', label: 'MPs & Electorates', icon: Users, ownerOnly: true }] : []),
    // Voting Guide - TPAUS only
    ...(tpausMode ? [{ href: '/voting-guide/manage', label: 'Voting Guide', icon: FileText }] : []),
    // Party Membership - ONP WA users + owner
    ...((onpWaMode || ownerMode) ? [{ href: '/membership/onp-wa/admin', label: 'Party Membership', icon: FileText }] : []),
    // Campaigns - for all users
    { href: '/campaigns', label: 'Campaigns', icon: FileText },
    // Leads - shown for all authenticated users (each org sees only their own leads)
    { href: '/collections', label: 'Leads', icon: FileText },
    // Only show Profile/Logout in dropdown if NOT hiding header buttons
    ...(!hideHeaderButtons ? [
      { href: '/profile', label: 'Profile', icon: User },
      { href: '#logout', label: 'Logout', icon: LogOut, action: () => { localStorage.removeItem('token'); window.location.href='/login'; } }
    ] : [])
  ];
  
  const currentPage = menuItems.find(item => item.href === location.pathname)?.label || 'Menu';
  
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-[#1E3A8A] rounded-sm hover:bg-[#1E3A8A]/90 transition-colors shadow-sm"
        data-testid="nav-dropdown-trigger"
      >
        <Menu className="w-4 h-4" />
        <span style={{ fontFamily: "'Inter', sans-serif" }}>{currentPage}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      
      {open && (
        <div className="absolute left-0 mt-2 w-64 bg-white border border-gray-200 rounded-sm shadow-xl z-50 py-2" data-testid="nav-dropdown-menu">
          {/* Logo header in dropdown */}
          <div className="px-4 py-3 border-b border-gray-100 mb-2">
            <p className="text-lg font-semibold text-[#1E3A8A]" style={{ fontFamily: "'Playfair Display', serif" }}>Civipen</p>
            <p className="text-xs text-gray-400 uppercase tracking-wider">Navigation</p>
          </div>
          
          {menuItems.map((item, idx) => (
            <div key={item.href}>
              {item.action ? (
                <button
                  onClick={() => { setOpen(false); item.action(); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-[#1E3A8A]/5 hover:text-[#1E3A8A] transition-colors"
                  data-testid={`nav-item-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className="w-4 h-4 text-gray-400" />
                  {item.label}
                </button>
              ) : (
                <a
                  href={item.href}
                  onClick={() => setOpen(false)}
                  className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                    location.pathname === item.href 
                      ? 'bg-[#1E3A8A]/10 text-[#1E3A8A] font-medium border-l-2 border-[#1E3A8A]' 
                      : 'text-gray-700 hover:bg-[#1E3A8A]/5 hover:text-[#1E3A8A]'
                  }`}
                  data-testid={`nav-item-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className={`w-4 h-4 ${location.pathname === item.href ? 'text-[#1E3A8A]' : 'text-gray-400'}`} />
                  {item.label}
                  {item.ownerOnly && (
                    <span className="ml-auto text-xs px-1.5 py-0.5 bg-[#B45309]/10 text-[#B45309] rounded-sm font-medium">Owner</span>
                  )}
                </a>
              )}
              {/* Add separator before Profile */}
              {idx === menuItems.length - 3 && !hideHeaderButtons && <div className="my-2 border-t border-gray-100" />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AdminShell = ({ children, hideHeaderButtons = false }) => { 
  loadOrgTheme();
  const p = tokenClaims();
  const stopImpersonate = ()=>{
    const backup=localStorage.getItem('owner_token_backup');
    if(backup){ localStorage.setItem('token', backup); localStorage.removeItem('owner_token_backup'); window.location.href='/owner'; }
    else { localStorage.removeItem('token'); window.location.href='/owner'; }
  };
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header with Art Deco styling */}
      <div className="bg-white border-b border-gray-200 shadow-sm" data-testid="global-header">
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-6">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <h1 
                className="text-xl font-semibold text-[#1E3A8A] tracking-tight"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Civipen
              </h1>
              {/* Art Deco accent */}
              <div className="w-1.5 h-1.5 rotate-45 bg-[#B45309]" />
            </div>
            
            {/* Navigation Dropdown */}
            <NavDropdown hideHeaderButtons={hideHeaderButtons} />
          </div>
          
          {!hideHeaderButtons && (
            <div className="flex items-center gap-4">
              {tokenHasImp() && (
                <button 
                  data-testid="global-stop-imp" 
                  className="text-sm text-[#B45309] hover:text-[#92400E] font-medium underline underline-offset-2"
                  onClick={stopImpersonate}
                >
                  Stop impersonating
                </button>
              )}
              <button 
                data-testid="global-logout" 
                onClick={()=>{ localStorage.removeItem('token'); window.location.href='/login'; }}
                className="text-sm text-gray-500 hover:text-[#1E3A8A] transition-colors"
              >
                Logout
              </button>
            </div>
          )}
          {hideHeaderButtons && tokenHasImp() && (
            <div className="flex items-center gap-3">
              <button 
                data-testid="global-stop-imp" 
                className="text-sm text-[#B45309] hover:text-[#92400E] font-medium underline underline-offset-2"
                onClick={stopImpersonate}
              >
                Stop impersonating
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Main content area */}
      <div className="p-6">
        {children}
      </div>
    </div>
  ); 
};

export default function App(){
  const [sessionExpired, setSessionExpired] = useState(false);
  useEffect(() => {
    const expired = () => setSessionExpired(true);
    window.addEventListener("auth-required", expired);
    return () => window.removeEventListener("auth-required", expired);
  }, []);
  return (
    <BrowserRouter>
      {sessionExpired && <div role="alert" data-testid="session-expired-notice" className="p-3 bg-amber-50 text-amber-950 flex flex-wrap items-center gap-3">
        Your session needs verification. Your unsaved work is still here.
        <Link data-testid="session-sign-in-link" to="/login?reauthenticate=1" target="_blank" className="underline">Sign in in a new tab</Link>
        <button data-testid="session-notice-dismiss" onClick={() => setSessionExpired(false)}>Dismiss</button>
      </div>}
      <Routes>
        <Route path="/terms" element={<Legal />} />
        <Route path="/privacy" element={<Legal />} />
        <Route path="/membership/onp-wa/terms" element={<Legal />} />
        <Route path="/membership/onp-wa/privacy" element={<Legal />} />
        <Route path="/login" element={<Login />} />
        <Route path="/owner" element={<RequireOwner><AdminShell><OwnerDashboard /></AdminShell></RequireOwner>} />
        <Route path="/campaigns" element={<RequireAuth><AdminShell><Campaigns /></AdminShell></RequireAuth>} />
        <Route path="/analytics" element={<RequireAuth><AdminShell><Analytics /></AdminShell></RequireAuth>} />
        <Route path="/analytics/:id" element={<RequireAuth><AdminShell><Analytics /></AdminShell></RequireAuth>} />
        <Route path="/collections" element={<RequireAuth><AdminShell><Collections /></AdminShell></RequireAuth>} />
        <Route path="/profile" element={<RequireAuth><AdminShell><Profile /></AdminShell></RequireAuth>} />
        <Route path="/orgs-users" element={<RequireOwner><AdminShell><OrgsUsers /></AdminShell></RequireOwner>} />
        {/* Custom Widgets Hub - Owner only */}
        <Route path="/custom-widgets" element={<RequireOwner><AdminShell hideHeaderButtons={true}><CustomWidgets /></AdminShell></RequireOwner>} />
        {/* Functional Demo - Public embeddable page (full campaign creation demo) */}
        <Route path="/functional-demo" element={<FunctionalDemo />} />
        {/* Election Simulator - Owner only widget */}
        <Route path="/election-simulator" element={<RequireOwner><ElectionSimulator /></RequireOwner>} />
        <Route path="/widget/:id" element={<Widget />} />
        {/* GIS Electorate Test - Public for testing */}
        <Route path="/gis-test" element={<GISTest />} />
        {/* Voting Guide - Public widget for TPAUS */}
        <Route path="/voting-guide" element={<VotingGuide />} />
        {/* Voting Guide Management - TPAUS users only */}
        <Route path="/voting-guide/manage" element={<RequireAuth><AdminShell><VotingGuideManage /></AdminShell></RequireAuth>} />
        {/* Party Membership - public widget for ONP-WA */}
        <Route path="/membership/onp-wa" element={<MembershipWidget />} />
        <Route path="/m/onp-wa" element={<MembershipWidget />} />
        {/* Party Membership admin - ONP-WA users + owner */}
        <Route path="/membership/onp-wa/admin" element={<RequireOnpWa><AdminShell><PartyMembership /></AdminShell></RequireOnpWa>} />
        <Route path="*" element={<Navigate to="/campaigns" replace />} />
      </Routes>
      <Toaster position="top-center" richColors data-testid="global-toaster" />
    </BrowserRouter>
  );
}
